import Close from "@mui/icons-material/Close";
import { Box, Dialog, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ExternalLink from "../../../../../assets/images/icons/ExternalLink";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import CopyApiTokenButton from "../../../../../components/common/OverviewFooter/CopyApiTokenButton";
import LinkConstants from "../../../../../constants/LinkConstants";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../../constants/billing";
import { useAppStore } from "../../../../../hooks/useStores";
import AttributionDiagram from "../AttributionDiagram/AttributionDiagram";
import styles from "./AttributionArchitectureCard.module.scss";

interface AttributionArchitectureCardProps {
	type: "fullPrivacy" | "noComputeCost";
	className?: string;
}

const AttributionArchitectureCard = ({ type, className }: AttributionArchitectureCardProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "models.attributionService.chooseArchitecture" });
	const { authStore } = useAppStore();
	const [showUpgradePopup, setShowUpgradePopup] = useState(false);

	const isFullPrivacy = type === "fullPrivacy";
	const cardKey = isFullPrivacy ? "fullPrivacy" : "noComputeCost";

	const hasCustomSubscription = authStore.orgSubscription?.plan_name === ORG_SUBSCRIPTION_PLANS.custom.name;

	const handleInstallClick = () => {
		hasCustomSubscription
			? window.open(LinkConstants.BRIA_ATTRIBUTION_SERVICE_DOCS, "_blank")
			: setShowUpgradePopup(true);
	};

	const handleContactUs = () => {
		window.open(LinkConstants.CONTACT_US_FORM_LINK, "_blank");
	};

	const handleViewApiDocsClick = () => {
		if (isFullPrivacy) {
			window.open(LinkConstants.BRIA_ATTRIBUTION_SERVICE_DOCS_BY_EMBEDDING, "_blank");
		} else {
			window.open(LinkConstants.BRIA_ATTRIBUTION_SERVICE_DOCS_BY_IMAGE, "_blank");
		}
	};

	return (
		<Box className={`${styles.card} ${className || ""}`}>
			<Typography variant="h5" className={styles.cardTitle}>
				{t(`${cardKey}.title`)}
			</Typography>

			<Typography className={styles.subTitle}>{t("howItWorks")}</Typography>

			<Box className={styles.steps}>
				<Box className={styles.step}>
					<Typography className={styles.stepNumber}>{t("step", { stepNumber: 1 })}</Typography>
					<Typography>{t(`${cardKey}.step1`)}</Typography>
				</Box>
				<Box className={styles.step}>
					<Typography className={styles.stepNumber}>{t("step", { stepNumber: 2 })}</Typography>
					<Typography>{t(`${cardKey}.step2`)}</Typography>
				</Box>
			</Box>

			<AttributionDiagram type={type} />

			<Typography className={styles.subTitle}>{t("howToSetup")}</Typography>

			<Box className={styles.setupSteps}>
				{isFullPrivacy && (
					<Box className={styles.setupItem}>
						<Box className={styles.setupStep}>
							<Typography className={styles.stepNumber}>{t("setupSteps.number1")}</Typography>
							<Typography className={styles.stepText}>{t(`${cardKey}.install`)}</Typography>
						</Box>
						<BriaButton buttonType="primaryMedium" className={styles.button} onClick={handleInstallClick}>
							{t(`${cardKey}.installButton`)}
						</BriaButton>
					</Box>
				)}

				<Box className={styles.setupItem}>
					<Box className={styles.setupStep}>
						{isFullPrivacy && (
							<Typography className={styles.stepNumber}>{t("setupSteps.number2")}</Typography>
						)}
						<Typography className={styles.stepText}>{t(`${cardKey}.integrate`)}</Typography>
					</Box>
					<CopyApiTokenButton />
				</Box>
			</Box>

			<Box className={styles.apiLink}>
				<BriaButton
					onClick={handleViewApiDocsClick}
					className={styles.viewApiDocsButton}
					buttonType="textMedium"
				>
					<ExternalLink /> {t("viewApiDocs")}
				</BriaButton>
			</Box>

			{/* Attribution License Upgrade Popup */}
			<Dialog
				open={showUpgradePopup}
				onClose={() => setShowUpgradePopup(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{
					className: styles.upgradePopup,
				}}
			>
				<Box className={styles.upgradePopupContainer}>
					<IconButton onClick={() => setShowUpgradePopup(false)} className={styles.closeButton}>
						<Close />
					</IconButton>
					<Typography className={styles.upgradePopupTitle}>{t("upgradePopup.title")}</Typography>
					<Typography className={styles.upgradePopupDescription}>{t("upgradePopup.description")}</Typography>
					<Box className={styles.upgradePopupButtons}>
						<BriaButton buttonType="secondaryMedium" onClick={() => setShowUpgradePopup(false)}>
							{t("upgradePopup.cancelButton")}
						</BriaButton>
						<BriaButton buttonType="primaryMedium" onClick={handleContactUs}>
							{t("upgradePopup.contactUsButton")}
						</BriaButton>
					</Box>
				</Box>
			</Dialog>
		</Box>
	);
};

export default AttributionArchitectureCard;
